import React, { useState, useEffect, useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import { utils } from "../../utils/Utils.jsx";
import { portfolioAPI } from "../../apis/PortfolioAPI.jsx";
import { swapAPI } from "../../apis/SwapAPI.jsx";
import { companyCardsAPI } from "../../apis/CompanyCardsAPI.jsx";
import { Spinner } from "react-bootstrap";
import GeoIcon, { BloxColor } from "../../components/icons/GeoIcon.jsx";
import BloxEmptyState from "../../components/blox/BloxEmptyState.jsx";
import "./treasurySummary.css";
import "./assetFocus.css";

/* Asset emblem — SVG hexagonal icon from S3 for known tickers,
   colored circle fallback for fiat. Wrapper div handles alignment. */
const EMBLEM_TICKERS = new Set(["USDT","USDC","BTC","ETH","SOL","XRP","BNB","ADA","AVAX","DOT"]);
const AssetEmblem = ({ ticker, size = 24 }) => {
  if (EMBLEM_TICKERS.has(ticker)) {
    return <img src={`/emblems/${ticker}.svg`} alt="" style={{ width: size, height: size, flexShrink: 0, alignSelf: "center", verticalAlign: "middle", display: "block" }} />;
  }
  return <span style={{ width: size, height: size, minWidth: size, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, alignSelf: "center", borderRadius: "50%", background: "#22C55E1A", color: "#22C55E", fontSize: size * 0.35, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{ticker.slice(0,3)}</span>;
};

/* prettyTxnType — turn the API's SCREAMING_SNAKE_CASE transaction
   types into human-readable labels. Specific mappings for known
   types/subtypes; everything else falls through to a Title-Case
   conversion so we never show raw SNAKE_CASE in the UI. */
const KNOWN_TXN_TYPES = {
  DEPOSIT: "Deposit",
  WITHDRAW: "Withdrawal",
  BUY: "Buy",
  SELL: "Sell",
  HOLD: "Processing",
  CONVERSION_DEPOSIT: "Conversion",
  CONVERSION_DEPOSIT_HOLD: "Conversion · processing",
  PAYME_DEPOSIT_HOLD: "Pay-me deposit · processing",
  CREDIT_ADJUSTMENT: "Credit adjustment",
  DEBIT_ADJUSTMENT: "Debit adjustment",
  TRANSFER_IN: "Transfer in",
  TRANSFER_OUT: "Transfer out",
  REFUND: "Refund",
  FEE: "Fee",
  REWARD: "Reward",
  /* Wire-transfer enum (BACKEND_TERMINAL_SPEC_V3 §I.1). Renders when
     BE delivers explicit wire types instead of DEBIT_ADJUSTMENT. */
  WIRE_OUT_DOMESTIC: "Outgoing Domestic Transfer",
  WIRE_OUT_INTERNATIONAL: "Outgoing International Transfer",
  WIRE_IN_DOMESTIC: "Incoming Domestic Transfer",
  WIRE_IN_INTERNATIONAL: "Incoming International Transfer",
};

/* Hash-link helpers (V3 §H.1). Used by the slideover when the
   transaction object carries `tx_hash` and `chain`. Unknown chains
   render the hash as plain mono text. */
const EXPLORER_BY_CHAIN = {
  ETH:   (h) => `https://etherscan.io/tx/${h}`,
  BSC:   (h) => `https://bscscan.com/tx/${h}`,
  MATIC: (h) => `https://polygonscan.com/tx/${h}`,
  ARB:   (h) => `https://arbiscan.io/tx/${h}`,
  OP:    (h) => `https://optimistic.etherscan.io/tx/${h}`,
  AVAX:  (h) => `https://snowtrace.io/tx/${h}`,
  BASE:  (h) => `https://basescan.org/tx/${h}`,
  SOL:   (h) => `https://solscan.io/tx/${h}`,
  TRX:   (h) => `https://tronscan.org/#/transaction/${h}`,
  BTC:   (h) => `https://mempool.space/tx/${h}`,
};
function explorerUrl(chain, hash) {
  if (!chain || !hash) return null;
  const fn = EXPLORER_BY_CHAIN[String(chain).toUpperCase()];
  return fn ? fn(hash) : null;
}

/* Virtual-asset detection — controls whether the slideover offers
   the Sell action chip for credit transactions. Mirrors EMBLEM_TICKERS
   but extended with the broader set the platform supports. */
const VIRTUAL_TICKERS = new Set([
  "USDT","USDC","BTC","ETH","SOL","XRP","BNB","ADA","AVAX","DOT","TRX","MATIC","LINK","ARB","OP","DAI","BUSD",
]);
function isVirtualAsset(ticker) {
  return ticker && VIRTUAL_TICKERS.has(String(ticker).toUpperCase());
}
const KNOWN_TXN_SUBTYPES = {
  PAY_VENDOR: "Vendor Payment",
  SEND_TO_PEER: "Peer Transfer",
  RECEIVED_FROM_PEER: "Received",
  BLOXCARD_ADD_FUNDS: "Card funding",
  BLOXCARD_REMOVE_FUNDS: "Card refund",
  PAYME_DEPOSIT: "Pay-me deposit",
  CONVERSION_DEPOSIT: "Conversion",
  STRIPE_PAYMENT: "Stripe payment",
};

/* prettyTxnSubtype — friendlier display for the raw SCREAMING_SNAKE_CASE
   subtype the backend sends. Returns the type's pretty name if subtype
   is empty so the slide-over and PDF have something useful to show. */
export function prettyTxnSubtype(txn) {
  if (!txn) return "";
  const sub = txn.transaction_subtype;
  if (sub && KNOWN_TXN_SUBTYPES[sub]) return KNOWN_TXN_SUBTYPES[sub];
  if (sub) return snakeToTitle(sub);
  if (txn.transaction_type && KNOWN_TXN_TYPES[txn.transaction_type]) {
    return KNOWN_TXN_TYPES[txn.transaction_type];
  }
  return snakeToTitle(txn.transaction_type || "");
}
function snakeToTitle(s) {
  return String(s)
    .toLowerCase()
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}
export function prettyTxnType(txn) {
  if (!txn) return "";
  if (txn.transaction_subtype && KNOWN_TXN_SUBTYPES[txn.transaction_subtype]) {
    return KNOWN_TXN_SUBTYPES[txn.transaction_subtype];
  }
  if (txn.transaction_type && KNOWN_TXN_TYPES[txn.transaction_type]) {
    return KNOWN_TXN_TYPES[txn.transaction_type];
  }
  return snakeToTitle(txn.transaction_type || "");
}

/* formatAssetQty — sensible decimal precision for a balance figure.
   utils.roundDecimals truncates BTC to 5 decimals which loses dust.
   For sub-1 amounts we widen precision so 0.00214 stays 0.00214 not
   0.0. For >= 1, we use the platform's roundDecimals (currency-
   aware whole/decimal rules). */
export function formatAssetQty(currency, qty, utilsRef) {
  const num = Number(qty);
  if (!Number.isFinite(num)) return "0";
  const abs = Math.abs(num);
  if (abs === 0) return "0";
  if (abs >= 1) return utilsRef.roundDecimals(currency, num);
  /* sub-1: scale precision to magnitude so dust reads precisely.
     Comma-grouped + no trailing zeros via the central formatter. */
  if (abs >= 0.01) return utilsRef.formatNumber(num, 4);
  if (abs >= 0.0001) return utilsRef.formatNumber(num, 6);
  return utilsRef.formatNumber(num, 8);
}

/* flattenTxns — robust adapter for the transaction history API.
   The backend has shipped multiple shapes for the same data:
     A) [{ holdings: [{ base_currency, transactions: [...] }] }]
     B) { holdings: [...] }
     C) [...] flat
     D) { content: [...] } paginated wrapper
   Walks each shape and returns a flat, currency-tagged, date-sorted
   list. fallbackCurrency tags any transaction missing base_currency
   (helpful for per-symbol fetches that may return transactions
   without a holding wrapper). */
export function flattenTxns(data, fallbackCurrency) {
  if (!data) return [];
  let candidates = [];
  if (Array.isArray(data) && data[0]?.holdings) {
    candidates = data.flatMap((page) => page.holdings || []);
  } else if (data.holdings) {
    candidates = data.holdings;
  } else if (Array.isArray(data) && data[0]?.transactions) {
    candidates = data;
  } else if (Array.isArray(data)) {
    return data
      .map((t) => ({ ...t, base_currency: t.base_currency || fallbackCurrency }))
      .sort((a, b) => new Date(b.creation_time) - new Date(a.creation_time));
  } else if (Array.isArray(data.content)) {
    return data.content
      .map((t) => ({ ...t, base_currency: t.base_currency || fallbackCurrency }))
      .sort((a, b) => new Date(b.creation_time) - new Date(a.creation_time));
  }
  const list = [];
  candidates.forEach((h) => {
    const cur = h.base_currency || fallbackCurrency;
    (h.transactions || []).forEach((t) => {
      if (t) list.push({ ...t, base_currency: t.base_currency || cur });
    });
  });
  list.sort((a, b) => new Date(b.creation_time) - new Date(a.creation_time));
  return list;
}

/* ── Transaction Detail Slide-Over ── */
export const TransactionSlideOver = ({ txn, onClose, localizedText, config, onSellAsset }) => {
  /* eslint-disable-next-line no-unused-vars */
  const _localized = localizedText;
  const [downloading, setDownloading] = useState(false);
  if (!txn) return null;

  const isCredit = ["DEPOSIT", "BUY", "CONVERSION_DEPOSIT", "WIRE_IN_DOMESTIC", "WIRE_IN_INTERNATIONAL"].includes(txn.transaction_type)
    || txn.transaction_subtype === "RECEIVED_FROM_PEER";

  /* Wire-transfer affordance: BE delivers either explicit WIRE_* type
     (V3 §I.1 option A) or a `wire_kind` discriminator (option B).
     Used to decide whether to render the beneficiary/UETR/OMAD/IMAD
     block below. Defaults to false for non-wire transactions so the
     receipt stays compact. */
  const isWire = /^WIRE_(IN|OUT)_/.test(String(txn.transaction_type || "")) || !!txn.wire_kind;

  /* On-chain affordance: virtual-asset credit → Sell chip routes the
     user to Convert with FROM pre-filled. The chip stays hidden when
     the caller hasn't threaded onSellAsset, so the existing standalone
     usage (transaction history page) is unaffected. */
  const canSell = isCredit && isVirtualAsset(txn.base_currency) && typeof onSellAsset === "function";
  const hashLink = explorerUrl(txn.chain, txn.tx_hash);

  const fees = (txn.childTransactions || []).reduce((acc, child) => {
    const key = child.base_currency || "USD";
    acc[key] = (acc[key] || 0) + Math.abs(child.qty || 0);
    return acc;
  }, {});

  const txId = txn.transaction_id || txn.id || "";
  const downloadReceipt = async () => {
    setDownloading(true);
    try {
      const fileName = "blox-receipt-" + (txId || Date.now());
      await utils.generatePDFFromReceipt(fileName, () => {});
    } catch (e) {
      console.log("receipt download err", e);
    } finally {
      setDownloading(false);
    }
  };

  const companyName = (config?.company_display_name || process.env.REACT_APP_COMPANY_NAME || "BLOX")
    .toString()
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

  return (
    <div className="txn-slideover-overlay" onClick={onClose}>
      <div className="txn-slideover" onClick={(e) => e.stopPropagation()}>
        <div className="txn-slideover-header">
          <span className="treasury-section-eyebrow">
            <span className="treasury-eyebrow-line" />
            Transaction Detail
          </span>
          <div className="txn-slideover-header-actions">
            {canSell && (
              <button
                type="button"
                className="txn-slideover-download"
                onClick={() => {
                  onSellAsset(txn.base_currency);
                  onClose && onClose();
                }}
                title={`Sell received ${txn.base_currency}`}
                aria-label={`Sell received ${txn.base_currency}`}
              >
                ↗ Sell
              </button>
            )}
            <button
              type="button"
              className="txn-slideover-download"
              onClick={downloadReceipt}
              disabled={downloading}
              title="Download receipt"
              aria-label="Download receipt"
            >
              {downloading ? "…" : "↓ Receipt"}
            </button>
            <button className="txn-slideover-close" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="txn-slideover-amount-row">
          <span className={"txn-slideover-amount " + (isCredit ? "credit" : "debit")}>
            {isCredit ? "+" : "-"}
            {utils.roundDecimals(txn.base_currency, Math.abs(txn.qty || txn.total_main_qty || 0))}
            <span className="txn-slideover-currency">{txn.base_currency}</span>
          </span>
        </div>

        <div className="txn-slideover-grid">
          {(txn.transaction_id || txn.id) && (
            <div className="txn-slideover-field txn-slideover-field-full">
              <span className="txn-slideover-label">Transaction ID</span>
              <span className="txn-slideover-value txn-mono txn-slideover-id">
                {txn.transaction_id || txn.id}
                <button
                  type="button"
                  className="txn-slideover-copy"
                  title="Copy"
                  onClick={(e) => {
                    e.stopPropagation();
                    const id = txn.transaction_id || txn.id;
                    if (navigator.clipboard) navigator.clipboard.writeText(String(id));
                    /* Brief visual confirmation. */
                    const btn = e.currentTarget;
                    const prev = btn.textContent;
                    btn.textContent = "✓";
                    setTimeout(() => { btn.textContent = prev; }, 1200);
                  }}
                >Copy</button>
              </span>
            </div>
          )}
          <div className="txn-slideover-field">
            <span className="txn-slideover-label">Type</span>
            <span className="txn-slideover-value">{prettyTxnType(txn)}</span>
          </div>
          {txn.transaction_subtype && (
            <div className="txn-slideover-field">
              <span className="txn-slideover-label">Category</span>
              <span className="txn-slideover-value">{prettyTxnSubtype(txn)}</span>
            </div>
          )}
          <div className="txn-slideover-field">
            <span className="txn-slideover-label">Date</span>
            <span className="txn-slideover-value">
              {new Date(txn.creation_time).toLocaleString(undefined, {
                year: "numeric", month: "short", day: "numeric",
                hour: "2-digit", minute: "2-digit"
              })}
            </span>
          </div>
          {(txn.from_display || txn.from) && (
            <div className="txn-slideover-field">
              <span className="txn-slideover-label">From</span>
              <span className={"txn-slideover-value" + (txn.from_display ? "" : " txn-mono")}>
                {txn.from_display || txn.from}
              </span>
            </div>
          )}
          {txn.to && (
            <div className="txn-slideover-field">
              <span className="txn-slideover-label">To</span>
              <span className="txn-slideover-value txn-mono">{txn.to}</span>
            </div>
          )}
          {txn.description && (
            <div className="txn-slideover-field txn-slideover-field-full">
              <span className="txn-slideover-label">Description</span>
              <span className="txn-slideover-value">{txn.description}</span>
            </div>
          )}
          {txn.price && (
            <div className="txn-slideover-field">
              <span className="txn-slideover-label">Price</span>
              <span className="txn-slideover-value txn-mono">${utils.roundDecimals("USD", txn.price)}</span>
            </div>
          )}

          {/* On-chain block (V3 §H.1). Renders only when the BE has
              echoed the chain hash; otherwise the receipt looks
              identical to today. */}
          {txn.tx_hash && (
            <div className="txn-slideover-field txn-slideover-field-full">
              <span className="txn-slideover-label">
                {txn.chain ? `${String(txn.chain).toUpperCase()} Transaction Hash` : "Transaction Hash"}
              </span>
              <span className="txn-slideover-value txn-mono txn-slideover-id">
                {hashLink ? (
                  <a
                    href={hashLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="txn-slideover-hash-link"
                  >{txn.tx_hash}</a>
                ) : (
                  <span>{txn.tx_hash}</span>
                )}
                <button
                  type="button"
                  className="txn-slideover-copy"
                  title="Copy"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (navigator.clipboard) navigator.clipboard.writeText(String(txn.tx_hash));
                    const btn = e.currentTarget;
                    const prev = btn.textContent;
                    btn.textContent = "✓";
                    setTimeout(() => { btn.textContent = prev; }, 1200);
                  }}
                >Copy</button>
              </span>
            </div>
          )}
          {txn.from_address && (
            <div className="txn-slideover-field">
              <span className="txn-slideover-label">Sender wallet</span>
              <span className="txn-slideover-value txn-mono">{txn.from_address}</span>
            </div>
          )}
          {txn.to_address && (
            <div className="txn-slideover-field">
              <span className="txn-slideover-label">Recipient wallet</span>
              <span className="txn-slideover-value txn-mono">{txn.to_address}</span>
            </div>
          )}
          {txn.confirmations != null && (
            <div className="txn-slideover-field">
              <span className="txn-slideover-label">Confirmations</span>
              <span className="txn-slideover-value txn-mono">{txn.confirmations}</span>
            </div>
          )}

          {/* Wire receipt block (V3 §I.2–§I.6). Renders only for
              wire-classified transactions so non-wire txns stay
              compact. Each field is conditional so partial BE
              rollouts still look clean. */}
          {isWire && txn.beneficiary_name && (
            <div className="txn-slideover-field">
              <span className="txn-slideover-label">Beneficiary</span>
              <span className="txn-slideover-value">{txn.beneficiary_name}</span>
            </div>
          )}
          {isWire && txn.beneficiary_bank_name && (
            <div className="txn-slideover-field">
              <span className="txn-slideover-label">Beneficiary bank</span>
              <span className="txn-slideover-value">{txn.beneficiary_bank_name}</span>
            </div>
          )}
          {isWire && txn.beneficiary_bank_swift && (
            <div className="txn-slideover-field">
              <span className="txn-slideover-label">SWIFT / BIC</span>
              <span className="txn-slideover-value txn-mono">{txn.beneficiary_bank_swift}</span>
            </div>
          )}
          {isWire && txn.beneficiary_bank_routing && (
            <div className="txn-slideover-field">
              <span className="txn-slideover-label">Routing / ABA</span>
              <span className="txn-slideover-value txn-mono">{txn.beneficiary_bank_routing}</span>
            </div>
          )}
          {isWire && txn.beneficiary_account_number && (
            <div className="txn-slideover-field">
              <span className="txn-slideover-label">Beneficiary account</span>
              <span className="txn-slideover-value txn-mono">{txn.beneficiary_account_number}</span>
            </div>
          )}
          {isWire && txn.beneficiary_address && (
            <div className="txn-slideover-field txn-slideover-field-full">
              <span className="txn-slideover-label">Beneficiary address</span>
              <span className="txn-slideover-value">{txn.beneficiary_address}</span>
            </div>
          )}
          {isWire && txn.beneficiary_bank_address && (
            <div className="txn-slideover-field txn-slideover-field-full">
              <span className="txn-slideover-label">Beneficiary bank address</span>
              <span className="txn-slideover-value">{txn.beneficiary_bank_address}</span>
            </div>
          )}
          {isWire && txn.special_instructions && (
            <div className="txn-slideover-field txn-slideover-field-full">
              <span className="txn-slideover-label">Special instructions</span>
              <span className="txn-slideover-value">{txn.special_instructions}</span>
            </div>
          )}
          {isWire && txn.uetr && (
            <div className="txn-slideover-field txn-slideover-field-full">
              <span className="txn-slideover-label">UETR (SWIFT)</span>
              <span className="txn-slideover-value txn-mono txn-slideover-id">
                {txn.uetr}
                <button
                  type="button"
                  className="txn-slideover-copy"
                  title="Copy"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (navigator.clipboard) navigator.clipboard.writeText(String(txn.uetr));
                    const btn = e.currentTarget;
                    const prev = btn.textContent;
                    btn.textContent = "✓";
                    setTimeout(() => { btn.textContent = prev; }, 1200);
                  }}
                >Copy</button>
              </span>
            </div>
          )}
          {isWire && txn.omad && (
            <div className="txn-slideover-field">
              <span className="txn-slideover-label">OMAD</span>
              <span className="txn-slideover-value txn-mono">{txn.omad}</span>
            </div>
          )}
          {isWire && txn.imad && (
            <div className="txn-slideover-field">
              <span className="txn-slideover-label">IMAD</span>
              <span className="txn-slideover-value txn-mono">{txn.imad}</span>
            </div>
          )}
        </div>

        {Object.keys(fees).length > 0 && (
          <div className="txn-slideover-fees">
            <span className="txn-slideover-label">Fees</span>
            {Object.entries(fees).map(([curr, amt]) => (
              <span key={curr} className="txn-slideover-fee-item">
                {utils.roundDecimals(curr, amt)} {curr}
              </span>
            ))}
          </div>
        )}

        <div className="txn-slideover-status">
          {(() => {
            /* Status pill prefers explicit wire_status (V3 §I.6) so a
               wire that's been submitted but not yet settled shows the
               truthful in-flight state instead of a green "Completed".
               Falls back to the legacy HOLD→Processing rule. */
            const ws = txn.wire_status;
            if (ws === "settled" || ws === "sent") {
              return <div className="txn-status-pill completed">{ws === "settled" ? "Settled" : "Sent"}</div>;
            }
            if (ws === "submitted" || ws === "accepted") {
              return <div className="txn-status-pill pending">{ws === "accepted" ? "Accepted" : "Submitted"}</div>;
            }
            if (ws === "rejected" || ws === "returned") {
              return <div className="txn-status-pill pending">{ws === "rejected" ? "Rejected" : "Returned"}</div>;
            }
            return (
              <div className={"txn-status-pill " + (txn.transaction_type === "HOLD" ? "pending" : "completed")}>
                {txn.transaction_type === "HOLD" ? "Processing" : "Completed"}
              </div>
            );
          })()}
        </div>

        {/* Hidden printable receipt — html2canvas in
            utils.generatePDFFromReceipt reads #pdf and rasterizes it.
            display:none here, flipped to display:block by the util's
            onclone hook just for capture. Branded for the active tenant. */}
        <div id="hidden-div" style={{ display: "none" }}>
          <div
            id="pdf"
            style={{
              width: 595, fontFamily: "Arial, sans-serif",
              color: "#0E1218", background: "#FFFFFF", padding: 40,
            }}
          >
            <div style={{ borderBottom: "2px solid #22C55E", paddingBottom: 16, marginBottom: 24 }}>
              <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: -0.5 }}>{companyName}</div>
              <div style={{ fontSize: 11, color: "#475569", marginTop: 4, letterSpacing: 1, textTransform: "uppercase" }}>
                Transaction Receipt
              </div>
            </div>

            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 11, color: "#475569", letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>
                {isCredit ? "Amount received" : "Amount sent"}
              </div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>
                {isCredit ? "+" : "-"}
                {utils.roundDecimals(txn.base_currency, Math.abs(txn.qty || txn.total_main_qty || 0))}
                <span style={{ fontSize: 14, color: "#475569", marginLeft: 6 }}>{txn.base_currency}</span>
              </div>
            </div>

            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <tbody>
                {txId && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569", verticalAlign: "top", width: 140 }}>Transaction ID</td>
                    <td style={{ padding: "8px 0", fontFamily: "monospace", wordBreak: "break-all" }}>{txId}</td>
                  </tr>
                )}
                <tr>
                  <td style={{ padding: "8px 0", color: "#475569" }}>Type</td>
                  <td style={{ padding: "8px 0" }}>{prettyTxnType(txn)}</td>
                </tr>
                {txn.transaction_subtype && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569" }}>Category</td>
                    <td style={{ padding: "8px 0" }}>{prettyTxnSubtype(txn)}</td>
                  </tr>
                )}
                <tr>
                  <td style={{ padding: "8px 0", color: "#475569" }}>Date</td>
                  <td style={{ padding: "8px 0" }}>
                    {new Date(txn.creation_time).toLocaleString(undefined, {
                      year: "numeric", month: "short", day: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </td>
                </tr>
                {(txn.from_display || txn.from) && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569", verticalAlign: "top" }}>From</td>
                    <td style={{
                      padding: "8px 0",
                      fontFamily: txn.from_display ? "Arial, sans-serif" : "monospace",
                      wordBreak: "break-all",
                    }}>{txn.from_display || txn.from}</td>
                  </tr>
                )}
                {txn.to && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569", verticalAlign: "top" }}>To</td>
                    <td style={{ padding: "8px 0", fontFamily: "monospace", wordBreak: "break-all" }}>{txn.to}</td>
                  </tr>
                )}
                {txn.description && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569", verticalAlign: "top" }}>Description</td>
                    <td style={{ padding: "8px 0" }}>{txn.description}</td>
                  </tr>
                )}
                {txn.price && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569" }}>Price</td>
                    <td style={{ padding: "8px 0", fontFamily: "monospace" }}>
                      ${utils.roundDecimals("USD", txn.price)}
                    </td>
                  </tr>
                )}
                {/* On-chain block (V3 §H.1) — PDF mirror. */}
                {txn.tx_hash && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569", verticalAlign: "top" }}>
                      {txn.chain ? `${String(txn.chain).toUpperCase()} Tx Hash` : "Tx Hash"}
                    </td>
                    <td style={{ padding: "8px 0", fontFamily: "monospace", wordBreak: "break-all" }}>{txn.tx_hash}</td>
                  </tr>
                )}
                {txn.from_address && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569", verticalAlign: "top" }}>Sender wallet</td>
                    <td style={{ padding: "8px 0", fontFamily: "monospace", wordBreak: "break-all" }}>{txn.from_address}</td>
                  </tr>
                )}
                {txn.to_address && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569", verticalAlign: "top" }}>Recipient wallet</td>
                    <td style={{ padding: "8px 0", fontFamily: "monospace", wordBreak: "break-all" }}>{txn.to_address}</td>
                  </tr>
                )}
                {/* Wire receipt block (V3 §I.2–§I.6) — PDF mirror. */}
                {isWire && txn.beneficiary_name && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569", verticalAlign: "top" }}>Beneficiary</td>
                    <td style={{ padding: "8px 0" }}>{txn.beneficiary_name}</td>
                  </tr>
                )}
                {isWire && txn.beneficiary_bank_name && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569", verticalAlign: "top" }}>Beneficiary bank</td>
                    <td style={{ padding: "8px 0" }}>{txn.beneficiary_bank_name}</td>
                  </tr>
                )}
                {isWire && txn.beneficiary_bank_swift && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569" }}>SWIFT / BIC</td>
                    <td style={{ padding: "8px 0", fontFamily: "monospace" }}>{txn.beneficiary_bank_swift}</td>
                  </tr>
                )}
                {isWire && txn.beneficiary_bank_routing && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569" }}>Routing / ABA</td>
                    <td style={{ padding: "8px 0", fontFamily: "monospace" }}>{txn.beneficiary_bank_routing}</td>
                  </tr>
                )}
                {isWire && txn.beneficiary_account_number && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569" }}>Beneficiary account</td>
                    <td style={{ padding: "8px 0", fontFamily: "monospace" }}>{txn.beneficiary_account_number}</td>
                  </tr>
                )}
                {isWire && txn.beneficiary_address && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569", verticalAlign: "top" }}>Beneficiary address</td>
                    <td style={{ padding: "8px 0" }}>{txn.beneficiary_address}</td>
                  </tr>
                )}
                {isWire && txn.beneficiary_bank_address && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569", verticalAlign: "top" }}>Beneficiary bank address</td>
                    <td style={{ padding: "8px 0" }}>{txn.beneficiary_bank_address}</td>
                  </tr>
                )}
                {isWire && txn.special_instructions && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569", verticalAlign: "top" }}>Special instructions</td>
                    <td style={{ padding: "8px 0" }}>{txn.special_instructions}</td>
                  </tr>
                )}
                {isWire && txn.uetr && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569", verticalAlign: "top" }}>UETR (SWIFT)</td>
                    <td style={{ padding: "8px 0", fontFamily: "monospace", wordBreak: "break-all" }}>{txn.uetr}</td>
                  </tr>
                )}
                {isWire && txn.omad && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569" }}>OMAD</td>
                    <td style={{ padding: "8px 0", fontFamily: "monospace" }}>{txn.omad}</td>
                  </tr>
                )}
                {isWire && txn.imad && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569" }}>IMAD</td>
                    <td style={{ padding: "8px 0", fontFamily: "monospace" }}>{txn.imad}</td>
                  </tr>
                )}
                {Object.entries(fees).length > 0 && (
                  <tr>
                    <td style={{ padding: "8px 0", color: "#475569", verticalAlign: "top" }}>Fees</td>
                    <td style={{ padding: "8px 0" }}>
                      {Object.entries(fees).map(([curr, amt]) => `${utils.roundDecimals(curr, amt)} ${curr}`).join(" · ")}
                    </td>
                  </tr>
                )}
                <tr>
                  <td style={{ padding: "8px 0", color: "#475569" }}>Status</td>
                  {(() => {
                    /* PDF mirror of the wire_status pill above so the
                       printed receipt is truthful about in-flight wires. */
                    const ws = txn.wire_status;
                    let label = txn.transaction_type === "HOLD" ? "Processing" : "Completed";
                    let color = txn.transaction_type === "HOLD" ? "#F59E0B" : "#16A34A";
                    if (ws === "settled") { label = "Settled"; color = "#16A34A"; }
                    else if (ws === "sent") { label = "Sent"; color = "#16A34A"; }
                    else if (ws === "submitted") { label = "Submitted"; color = "#F59E0B"; }
                    else if (ws === "accepted") { label = "Accepted"; color = "#F59E0B"; }
                    else if (ws === "rejected") { label = "Rejected"; color = "#DC2626"; }
                    else if (ws === "returned") { label = "Returned"; color = "#DC2626"; }
                    return (
                      <td style={{ padding: "8px 0", fontWeight: 600, color }}>{label}</td>
                    );
                  })()}
                </tr>
              </tbody>
            </table>

            <div style={{ marginTop: 32, paddingTop: 16, borderTop: "1px solid #E2E8F0", fontSize: 10, color: "#94A3B8" }}>
              Generated by {companyName} on {new Date().toLocaleString(undefined, {
                year: "numeric", month: "short", day: "numeric",
                hour: "2-digit", minute: "2-digit",
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Asset Focus — inline drill-down replacing the cockpit ──
   When the user clicks a position, the cockpit is swapped for this
   focused view: emblem hero, qty + USD-equivalent + share-of-portfolio,
   four quick-action chips (Pay / Convert / Deposit / Withdraw) wired
   to the corresponding tabs, recent activity for THIS asset only, and
   a back/full-detail control. Stays entirely inside the dashboard
   tab content area. */
const AssetFocus = ({ position, txns, txnsLoading, onBack, onOpenFull, onTxnClick,
                      formatTxnType, isTxnCredit, setActiveTab,
                      setTreasurySub, setReceiveSub,
                      setPrefillConvertFrom, totalUsd,
                      hasMore, loadingMore, onLoadMore }) => {
  const ticker = position.data.base_currency;
  const qty = Math.abs(position.data.qty);
  const usd = position.value_usd != null ? Math.abs(position.value_usd) : null;
  const sharePct = (usd != null && totalUsd > 0)
    ? (usd / Math.abs(totalUsd)) * 100
    : null;

  const actions = [
    { label: "Convert", tab: "convert", icon: "exchange", color: "#22C55E" },
    { label: "Pay", tab: "pay", icon: "wire", color: "#06B6D4" },
    { label: "Deposit", tab: "deposit", icon: "wallet", color: "#22C55E" },
    { label: "Withdraw", tab: "withdraw", icon: "vault", color: "#22C55E" },
  ];

  return (
    <div className="asset-focus">
      <div className="asset-focus-topbar">
        <button className="asset-focus-back" onClick={onBack} aria-label="Back">
          <span className="asset-focus-back-arrow">←</span>
          Back to overview
        </button>
        <button className="asset-focus-fullbtn" onClick={onOpenFull}>
          Open full detail →
        </button>
      </div>

      <div className="asset-focus-hero">
        <div className="asset-focus-hero-left">
          <AssetEmblem ticker={ticker} size={56} />
          <div className="asset-focus-hero-info">
            <span className="asset-focus-eyebrow">
              <span className="asset-focus-eyebrow-line" />
              Position
            </span>
            <h2 className="asset-focus-ticker">{ticker}</h2>
          </div>
        </div>
        <div className="asset-focus-hero-right">
          <div className="asset-focus-stat">
            <span className="asset-focus-stat-label">Quantity</span>
            <span className="asset-focus-stat-value">{utils.roundDecimals(ticker, qty)}</span>
            <span className="asset-focus-stat-meta">{ticker}</span>
          </div>
          {usd != null && (
            <div className="asset-focus-stat">
              <span className="asset-focus-stat-label">USD value</span>
              <span className="asset-focus-stat-value asset-focus-stat-value-accent">
                ${utils.roundDecimals("USD", usd)}
              </span>
              {sharePct != null && (
                <span className="asset-focus-stat-meta">{sharePct.toFixed(1)}% of portfolio</span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="asset-focus-actions">
        {actions.map((a) => (
          <button
            key={a.tab}
            className="asset-focus-action"
            style={{ "--action-color": a.color }}
            onClick={() => {
              /* Convert prefills the FROM currency with this asset
                 so the user lands in the form ready to act. */
              if (a.tab === "convert" && setPrefillConvertFrom) {
                setPrefillConvertFrom(ticker);
              }
              onBack();
              /* Map action label → top-level tab + sub-tab. The labels
                 are sub-tabs of either Treasury (convert/withdraw) or
                 Receive (deposit); Pay is the only top-level one. */
              if (a.tab === "convert") {
                setActiveTab && setActiveTab("treasury");
                setTreasurySub && setTreasurySub("convert");
              } else if (a.tab === "withdraw") {
                setActiveTab && setActiveTab("treasury");
                setTreasurySub && setTreasurySub("withdraw");
              } else if (a.tab === "deposit") {
                setActiveTab && setActiveTab("receive");
                setReceiveSub && setReceiveSub("deposit");
              } else if (a.tab === "pay") {
                setActiveTab && setActiveTab("pay");
              }
            }}
          >
            <span className="asset-focus-action-icon">
              <GeoIcon shape={a.icon} color={a.color} size={20} className="" />
            </span>
            <span>{a.label}</span>
          </button>
        ))}
      </div>

      <div className="asset-focus-activity">
        <div className="asset-focus-activity-header">
          <span className="asset-focus-eyebrow">
            <span className="asset-focus-eyebrow-line" />
            {ticker} activity
          </span>
          <span className="asset-focus-activity-meta">
            {txnsLoading
              ? "Loading…"
              : txns.length === 0
                ? "No recent activity"
                : `${txns.length} recent`}
          </span>
        </div>
        {txnsLoading ? (
          <div className="asset-focus-empty">
            <span>Loading {position.data.base_currency} transactions…</span>
          </div>
        ) : txns.length > 0 ? (
          <div className="asset-focus-activity-list">
            {txns.map((txn, i) => {
              const rawAmount = Number(txn.qty != null ? txn.qty : txn.total_main_qty);
              const isCredit = !Number.isNaN(rawAmount) && rawAmount > 0;
              const isDebit = !Number.isNaN(rawAmount) && rawAmount < 0;
              const cls = isCredit ? " credit" : isDebit ? " debit" : "";
              return (
                <div key={i} className="asset-focus-activity-row" onClick={() => onTxnClick(txn)}>
                  <div className="asset-focus-activity-left">
                    <span className={"asset-focus-activity-dot" + cls} />
                    <div className="asset-focus-activity-info">
                      <span className="asset-focus-activity-type">{formatTxnType(txn)}</span>
                      <span className="asset-focus-activity-date">
                        {new Date(txn.creation_time).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </span>
                    </div>
                  </div>
                  <div className="asset-focus-activity-right">
                    <span className={"asset-focus-activity-amount" + cls}>
                      {isCredit ? "+" : ""}
                      {utils.roundDecimals(ticker, rawAmount)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="asset-focus-empty">
            <span>No recent {ticker} transactions</span>
          </div>
        )}

        {/* Load-more — fetches the next paginated page when there's
            likely more (heuristic in TreasurySummary: the previous
            page returned a full PAGE_SIZE worth of rows). Hidden
            once the backend signals exhaustion or while the initial
            list is still loading. */}
        {!txnsLoading && hasMore && (
          <button
            type="button"
            className="asset-focus-load-more"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        )}
      </div>
    </div>
  );
};

/* ── Main Treasury Summary ── */
const TreasurySummary = ({ config, localizedText, token, setActiveTab,
                            setTreasurySub, setReceiveSub, setPrefillConvertFrom,
                            externalOpenAsset, onCloseAssetFocus, onOpenAssetFocus }) => {
  const history = useHistory();
  const [positions, setPositions] = useState([]);
  const [totalUsd, setTotalUsd] = useState(0);
  const [loading, setLoading] = useState(true);
  const [recentTxns, setRecentTxns] = useState([]);
  const [txnsLoading, setTxnsLoading] = useState(true);
  const [lockedRatesCount, setLockedRatesCount] = useState(0);
  const [nearestExpiry, setNearestExpiry] = useState(null);
  const [cardCount, setCardCount] = useState(0);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [localOpenAsset, setLocalOpenAsset] = useState(null);
  const [portfolioName, setPortfolioName] = useState(null);
  const [assetTxns, setAssetTxns] = useState([]);
  const [assetTxnsLoading, setAssetTxnsLoading] = useState(false);
  /* When parent provides externalOpenAsset (lifted state from Home),
     prefer it. Otherwise fall back to local state so this component
     still works standalone. */
  const openAsset = externalOpenAsset || localOpenAsset;
  const setOpenAsset = onOpenAssetFocus || setLocalOpenAsset;

  // Fetch ALL portfolio balances
  useEffect(() => {
    if (!token) return;
    portfolioAPI.getDefaultPortfolio(token).then((res) => {
      if (res.status === 200 && res.data) {
        setPortfolioName(res.data.name);
        portfolioAPI.getPortfolioBalances(token, res.data.name).then((balRes) => {
          if (balRes.status === 200) {
            const nonZero = balRes.data.filter((b) => b.qty !== 0);
            const usdItems = nonZero.filter((b) => b.base_currency === "USD");
            const nonUsd = nonZero.filter((b) => b.base_currency !== "USD");
            let usdTotal = usdItems.reduce((s, b) => s + b.qty, 0);
            const signs = nonUsd.map((b) => Math.sign(b.qty));

            Promise.all(
              nonUsd.map((b) =>
                swapAPI.convertEstimate(token, Math.abs(b.qty), b.base_currency, "USD", 0).catch(() => null)
              )
            ).then((conversions) => {
              const result = [];
              usdItems.forEach((b) => result.push({ data: b, value_usd: b.qty }));
              nonUsd.forEach((b, i) => {
                const conv = conversions[i];
                const usdVal = conv?.data?.amount_to_receive ? Number(conv.data.amount_to_receive) * signs[i] : null;
                if (usdVal != null) usdTotal += usdVal;
                result.push({ data: b, value_usd: usdVal });
              });
              result.sort((a, b) => Math.abs(b.value_usd || 0) - Math.abs(a.value_usd || 0));
              setPositions(result);
              setTotalUsd(usdTotal);
              setLoading(false);
            });
          }
        });
      }
    }).catch(() => setLoading(false));
  }, [token]);

  // Fetch recent transactions. Tries multiple known response shapes
  // (nested holdings, flat array, paginated content) so we don't
  // starve when the backend changes its envelope.
  useEffect(() => {
    if (!token) return;
    portfolioAPI.getTransactionHistoryPaginated(token, 0, 50).then((res) => {
      console.info("[Blox cockpit txns] status=" + res?.status, {
        keys: res?.data ? Object.keys(res.data) : null,
        sample: res?.data,
      });
      if (res.status === 200) {
        const list = flattenTxns(res.data);
        console.info("[Blox cockpit txns] flattened count=" + list.length);
        setRecentTxns(list);
      }
      setTxnsLoading(false);
    }).catch((e) => {
      console.info("[Blox cockpit txns] error", e?.response?.status, e?.response?.data);
      setTxnsLoading(false);
    });
  }, [token]);

  // Fetch locked FX rates count
  useEffect(() => {
    if (!token) return;
    swapAPI.getLockedRates(token).then((res) => {
      if (res.status === 200 && Array.isArray(res.data)) {
        const active = res.data.filter((r) => new Date(r.expires_on) > new Date());
        setLockedRatesCount(active.length);
        if (active.length > 0) {
          const nearest = active.reduce((min, r) =>
            new Date(r.expires_on) < new Date(min.expires_on) ? r : min
          );
          setNearestExpiry(new Date(nearest.expires_on));
        }
      }
    }).catch(() => {});
  }, [token]);

  // Fetch card count
  useEffect(() => {
    if (!token) return;
    companyCardsAPI.getCompanyCards(token).then((res) => {
      if (res.status === 200 && Array.isArray(res.data)) setCardCount(res.data.length);
    }).catch(() => {});
  }, [token]);

  const formatTxnType = prettyTxnType;

  const isTxnCredit = (txn) =>
    ["DEPOSIT", "BUY", "CONVERSION_DEPOSIT"].includes(txn.transaction_type)
    || txn.transaction_subtype === "RECEIVED_FROM_PEER";

  const getExpiryText = () => {
    if (!nearestExpiry) return "";
    const diff = nearestExpiry - new Date();
    if (diff <= 0) return "expired";
    const mins = Math.floor(diff / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  // Live countdown for locked rates
  const [, setTick] = useState(0);
  useEffect(() => {
    if (lockedRatesCount === 0) return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [lockedRatesCount]);

  /* ── Treasury Intelligence Engine ──
     Analyzes portfolio data and generates actionable insights.
     This is the differentiator — no competitor does this. */
  const generateInsights = () => {
    if (loading) return [];
    const insights = [];

    // 1. Idle balance detection — fiat sitting uninvested
    const usdPosition = positions.find((p) => p.data.base_currency === "USD");
    if (usdPosition && usdPosition.data.qty > 100) {
      insights.push({
        type: "opportunity",
        color: "#22C55E",
        title: "Idle USD detected",
        message: `$${utils.roundDecimals("USD", usdPosition.data.qty)} in USD is uninvested. Consider converting to a yield-bearing stablecoin.`,
        action: { label: "Convert", tab: "convert" },
      });
    }

    // 2. Concentration risk — single asset > 80% of portfolio
    if (positions.length > 1 && totalUsd > 0) {
      const largest = positions[0];
      const pct = Math.abs(largest.value_usd || 0) / Math.abs(totalUsd) * 100;
      if (pct > 80) {
        insights.push({
          type: "warning",
          color: "#FBBF24",
          title: "Concentration risk",
          message: `${largest.data.base_currency} represents ${pct.toFixed(0)}% of your portfolio. Consider diversifying across currencies.`,
          action: { label: "View assets", tab: "overview" },
        });
      }
    }

    // 3. Locked FX rates expiring soon
    if (lockedRatesCount > 0 && nearestExpiry) {
      const minsLeft = Math.floor((nearestExpiry - new Date()) / 60000);
      if (minsLeft > 0 && minsLeft < 30) {
        insights.push({
          type: "urgent",
          color: "#F87171",
          title: "FX rate expiring soon",
          message: `You have ${lockedRatesCount} locked rate${lockedRatesCount > 1 ? "s" : ""} — nearest expires in ${minsLeft} minutes. Execute or lose the rate.`,
          action: { label: "Go to Convert", tab: "convert" },
        });
      }
    }

    // 4. Negative balance alert
    const negatives = positions.filter((p) => p.data.qty < 0);
    if (negatives.length > 0) {
      const totalNeg = negatives.reduce((s, p) => s + Math.abs(p.value_usd || 0), 0);
      insights.push({
        type: "urgent",
        color: "#F87171",
        title: "Negative balance",
        message: `${negatives.map((n) => n.data.base_currency).join(", ")} ${negatives.length > 1 ? "have" : "has"} negative balance totaling -$${utils.roundDecimals("USD", totalNeg)}. Deposit to avoid fees.`,
        action: { label: "Deposit", tab: "deposit" },
      });
    }

    // 5. No cards — suggest card issuance
    if (cardCount === 0 && !loading) {
      insights.push({
        type: "info",
        color: "#06B6D4",
        title: "No corporate cards",
        message: "Issue a virtual card for your team to manage expenses with real-time controls.",
        action: { label: "Get a card", tab: "cards" },
      });
    }

    // 6. Multi-currency advantage — highlight what competitors can't do
    const uniqueCurrencies = positions.filter((p) => p.data.qty !== 0).length;
    if (uniqueCurrencies >= 3) {
      insights.push({
        type: "info",
        color: "#8B5CF6",
        title: `${uniqueCurrencies} currencies in one treasury`,
        message: "You're managing fiat and digital assets in a unified workspace. Lock FX rates, load cards, and pay vendors across borders — all from here.",
        action: null,
      });
    }

    return insights.slice(0, 3); // Max 3 insights
  };

  const insights = generateInsights();

  /* When the user clicks a position, replace the cockpit with an
     inline asset focus view instead of navigating to a full route.
     Keeps the user inside the dashboard tab content. */
  const openPosition = (pos) => {
    setOpenAsset(pos);
  };
  const closeAsset = () => {
    if (onCloseAssetFocus) onCloseAssetFocus();
    setLocalOpenAsset(null);
    setAssetTxns([]);
  };

  /* "Sell received asset" → routes to Treasury · Convert with the
     received asset pre-filled as FROM. Triggered from the slide-over
     for credit virtual-asset transactions. Falls back to a no-op
     when the parent didn't thread the tab handlers (so the standalone
     slideover usage stays safe). */
  const handleSellAsset = (ticker) => {
    if (!ticker) return;
    if (setPrefillConvertFrom) setPrefillConvertFrom(ticker);
    if (setActiveTab) setActiveTab("treasury");
    if (setTreasurySub) setTreasurySub("convert");
  };

  /* When AssetFocus opens, page through the paginated portfolio
     transaction history filtered to this symbol. Paginated rather
     than per-symbol so the user can load more and see the full
     history (per-symbol endpoint returned everything in one slow
     payload, then we'd cap at 8 rows for UX — that meant most
     activity was unreachable). */
  const PAGE_SIZE = 25;
  const [assetPage, setAssetPage] = useState(0);
  const [assetHasMore, setAssetHasMore] = useState(false);
  const [assetLoadingMore, setAssetLoadingMore] = useState(false);

  useEffect(() => {
    if (!openAsset || !token) return;
    setAssetTxnsLoading(true);
    setAssetPage(0);
    const ticker = openAsset.data.base_currency;
    portfolioAPI.getTransactionHistoryPaginated(token, 0, PAGE_SIZE)
      .then((res) => {
        if (res.status === 200) {
          const all = flattenTxns(res.data, ticker);
          const matching = all.filter((t) => t.base_currency === ticker);
          setAssetTxns(matching);
          /* Heuristic: if the broad page returned a full PAGE_SIZE,
             there's likely a next page worth loading. */
          const totalRowsOnPage = all.length;
          setAssetHasMore(totalRowsOnPage >= PAGE_SIZE);
        } else {
          setAssetTxns([]);
          setAssetHasMore(false);
        }
        setAssetTxnsLoading(false);
      })
      .catch((e) => {
        console.info("[Blox asset txns " + ticker + "] error", e?.response?.status, e?.response?.data);
        setAssetTxns([]);
        setAssetHasMore(false);
        setAssetTxnsLoading(false);
      });
  }, [openAsset, token]);

  const loadMoreAssetTxns = () => {
    if (!openAsset || assetLoadingMore || !assetHasMore) return;
    setAssetLoadingMore(true);
    const nextPage = assetPage + 1;
    const ticker = openAsset.data.base_currency;
    portfolioAPI.getTransactionHistoryPaginated(token, nextPage, PAGE_SIZE)
      .then((res) => {
        if (res.status === 200) {
          const all = flattenTxns(res.data, ticker);
          const matching = all.filter((t) => t.base_currency === ticker);
          setAssetTxns((prev) => [...prev, ...matching]);
          setAssetPage(nextPage);
          setAssetHasMore(all.length >= PAGE_SIZE);
        } else {
          setAssetHasMore(false);
        }
        setAssetLoadingMore(false);
      })
      .catch(() => {
        setAssetHasMore(false);
        setAssetLoadingMore(false);
      });
  };

  if (openAsset) {
    return (
      <div className="treasury-summary">
        <AssetFocus
          position={openAsset}
          txns={assetTxns}
          txnsLoading={assetTxnsLoading}
          onBack={closeAsset}
          onOpenFull={() => history.push(`/dashboard/portfolio/asset/${openAsset.data.base_currency}`)}
          onTxnClick={setSelectedTxn}
          formatTxnType={formatTxnType}
          isTxnCredit={isTxnCredit}
          setActiveTab={setActiveTab}
          setTreasurySub={setTreasurySub}
          setReceiveSub={setReceiveSub}
          setPrefillConvertFrom={setPrefillConvertFrom}
          totalUsd={totalUsd}
          hasMore={assetHasMore}
          loadingMore={assetLoadingMore}
          onLoadMore={loadMoreAssetTxns}
        />
        {selectedTxn && (
          <TransactionSlideOver
            txn={selectedTxn}
            onClose={() => setSelectedTxn(null)}
            localizedText={localizedText}
            config={config}
            onSellAsset={handleSellAsset}
          />
        )}
      </div>
    );
  }

  return (
    <div className="treasury-summary">
      {/* Two-column cockpit: Positions + Activity */}
      <div className="treasury-cockpit">
        {/* Left: Positions Table */}
        <div className="treasury-positions">
          <div className="treasury-section-header">
            <span className="treasury-section-eyebrow">
              <span className="treasury-eyebrow-line" />
              Positions
            </span>
            <Link to="/dashboard/portfolio/assets" className="treasury-section-link">View all →</Link>
          </div>
          {loading ? (
            <div className="treasury-loading">
              <div className="treasury-skeleton-row" /><div className="treasury-skeleton-row" /><div className="treasury-skeleton-row" />
            </div>
          ) : positions.length === 0 ? (
            <BloxEmptyState
              shape="vault"
              color={BloxColor.green}
              title="No positions yet"
              message="Fund your treasury to start managing balances across currencies."
              cta={{ label: "Make a deposit →", onClick: () => setActiveTab && setActiveTab("deposit") }}
              size="compact"
            />
          ) : (
            <div className="treasury-positions-list">
              {positions.map((pos, i) => (
                <div key={i} className="treasury-position-row" onClick={() => openPosition(pos)}>
                  <div className="treasury-position-left">
                    <AssetEmblem ticker={pos.data.base_currency} size={36} />
                    <div className="treasury-position-info">
                      <span className="treasury-position-currency">{pos.data.base_currency}</span>
                      <span className="treasury-position-qty">
                        {formatAssetQty(pos.data.base_currency, Math.abs(pos.data.qty), utils)}
                      </span>
                    </div>
                  </div>
                  <div className="treasury-position-right">
                    {pos.value_usd != null && (
                      <span className={"treasury-position-usd" + (pos.value_usd < 0 ? " negative" : "")}>
                        {pos.value_usd < 0 ? "-" : ""}${utils.roundDecimals("USD", Math.abs(pos.value_usd))}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Activity Feed */}
        <div className="treasury-activity">
          <div className="treasury-section-header">
            <span className="treasury-section-eyebrow">
              <span className="treasury-eyebrow-line" />
              Recent Activity
            </span>
            <Link to="/dashboard/portfolio/history" className="treasury-section-link">View all →</Link>
          </div>
          {txnsLoading ? (
            <div className="treasury-loading">
              <div className="treasury-skeleton-row" /><div className="treasury-skeleton-row" /><div className="treasury-skeleton-row" />
            </div>
          ) : recentTxns.length === 0 ? (
            <BloxEmptyState
              shape="ledger"
              color={BloxColor.green}
              title="No activity yet"
              message="Once you deposit, send, or convert, transactions will land here."
              size="compact"
            />
          ) : (
            <div className="treasury-activity-list">
              {recentTxns.slice(0, 5).map((txn, i) => {
                if (!txn) return null;
                const date = txn.creation_time ? new Date(txn.creation_time) : null;
                const dateLabel = date && !isNaN(date.getTime())
                  ? date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
                  : "—";
                /* Trust the qty sign from the API. Positive = credit
                   (green), negative = debit (red). Render the value
                   verbatim — no Math.abs, no sign-flip — so what the
                   user sees matches what the network returned. */
                const rawAmount = Number(txn.qty != null ? txn.qty : txn.total_main_qty);
                const isCredit = !Number.isNaN(rawAmount) && rawAmount > 0;
                const isDebit = !Number.isNaN(rawAmount) && rawAmount < 0;
                const cls = isCredit ? " credit" : isDebit ? " debit" : "";
                return (
                  <div key={i} className="treasury-activity-row" onClick={() => setSelectedTxn(txn)}>
                    <div className="treasury-activity-left">
                      <div className={"treasury-activity-indicator" + cls} />
                      <div className="treasury-activity-info">
                        <span className="treasury-activity-type">{formatTxnType(txn)}</span>
                        <span className="treasury-activity-date">{dateLabel}</span>
                      </div>
                    </div>
                    <div className="treasury-activity-right">
                      <span className={"treasury-activity-amount" + cls}>
                        {isCredit ? "+" : ""}
                        {utils.roundDecimals(txn.base_currency || "USD", rawAmount)}
                      </span>
                      <span className="treasury-activity-currency">{txn.base_currency || ""}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Transaction Detail Slide-Over */}
      {selectedTxn && (
        <TransactionSlideOver
          txn={selectedTxn}
          onClose={() => setSelectedTxn(null)}
          localizedText={localizedText}
          config={config}
          onSellAsset={handleSellAsset}
        />
      )}
    </div>
  );
};

export default TreasurySummary;
